/**
 * Talk to a Strategist — 50/50 split layout
 * Left: branded panel with grid lines, value props, social proof
 * Right: compact form, fixed viewport, no scroll
 */
import React, { useState, useRef, useEffect } from "react";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useLocation } from "wouter";
import { formSource } from "@/lib/formSource";
import { INDEXED_WORDMARK } from "@/lib/brand";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";

const STEPS = [
  { id: "contact", label: "Your Details" },
  { id: "company", label: "Your Company" },
  { id: "goals", label: "Your Goals" },
] as const;

const SERVICE_OPTIONS = [
  "SEO Agency", "Technical SEO", "SEO Audit & Strategy", "AI SEO",
  "Local SEO", "Content Production", "Programmatic SEO",
  "Link Building", "Web Design", "Web Development",
];
const BUDGET_OPTIONS = [
  "Under £2,000/mo", "£2,000 – £5,000/mo",
  "£5,000 – £10,000/mo", "£10,000 – £20,000/mo",
  "£20,000+/mo", "Not sure yet",
];
const REVENUE_OPTIONS = [
  "Under £500K", "£500K – £1M",
  "£1M – £5M", "£5M – £20M",
  "£20M+", "Prefer not to say",
];

// SVG grid pattern (matches .section-grid::before)
const GRID_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='162' height='162'%3E%3Crect width='162' height='162' fill='none'/%3E%3Cpath d='M162 0 L162 162 M0 162 L162 162' stroke='rgba(87,215,226,0.18)' stroke-width='1'/%3E%3C/svg%3E")`;

// ── Text input ──────────────────────────────────────────────────────────
function Field({ label, type = "text", value, onChange, placeholder, required, error }: {
  label: string; type?: string; value: string; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; error?: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, fontWeight: 600, fontFamily: ff, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: cyan }}> *</span>}
      </label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={{
          background: "rgba(255,255,255,0.04)", border: error ? "1px solid #ef4444" : "1px solid rgba(255,255,255,0.1)",
          borderRadius: 8, padding: "11px 14px", color: "white", fontSize: 15, fontFamily: ff,
          outline: "none", width: "100%", boxSizing: "border-box",
        }}
        onFocus={(e) => { if (!error) e.currentTarget.style.borderColor = "rgba(87,215,226,0.5)"; }}
        onBlur={(e) => { if (!error) e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
      />
      {error && <span style={{ color: "#f87171", fontSize: 11, fontFamily: ff }}>{error}</span>}
    </div>
  );
}

// ── 2-column grid radio ──────────────────────────────────────────────────
function GridSelect({ label, options, value, onChange, error }: {
  label: string; options: string[]; value: string; onChange: (v: string) => void; error?: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <label style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, fontWeight: 600, fontFamily: ff, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {label}<span style={{ color: cyan }}> *</span>
      </label>
      {error && <span style={{ color: "#f87171", fontSize: 11, fontFamily: ff }}>{error}</span>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button key={opt} type="button" onClick={() => onChange(opt)} style={{
              padding: "9px 12px", borderRadius: 7,
              border: active ? `1px solid ${cyan}` : "1px solid rgba(255,255,255,0.07)",
              background: active ? "rgba(87,215,226,0.08)" : "rgba(255,255,255,0.02)",
              color: active ? "white" : "rgba(255,255,255,0.45)",
              fontSize: 13, fontWeight: 500, fontFamily: ff, cursor: "pointer",
              textAlign: "left", display: "flex", alignItems: "center", gap: 8, transition: "all 0.12s",
            }}>
              <div style={{
                width: 11, height: 11, borderRadius: "50%", flexShrink: 0,
                border: active ? `2px solid ${cyan}` : "2px solid rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {active && <div style={{ width: 4, height: 4, borderRadius: "50%", background: cyan }} />}
              </div>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Pill selector ────────────────────────────────────────────────────────
function PillSelect({ label, options, selected, onToggle, error }: {
  label: string; options: string[]; selected: string[]; onToggle: (v: string) => void; error?: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <label style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, fontWeight: 600, fontFamily: ff, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {label}<span style={{ color: cyan }}> *</span>
      </label>
      {error && <span style={{ color: "#f87171", fontSize: 11, fontFamily: ff }}>{error}</span>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => onToggle(opt)} style={{
              padding: "6px 13px", borderRadius: 88,
              border: active ? `1px solid ${cyan}` : "1px solid rgba(255,255,255,0.09)",
              background: active ? "rgba(87,215,226,0.1)" : "transparent",
              color: active ? cyan : "rgba(255,255,255,0.45)",
              fontSize: 12, fontWeight: 500, fontFamily: ff, cursor: "pointer", transition: "all 0.12s",
            }}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Country codes + phone input ──────────────────────────────────────────
const COUNTRY_CODES = [
  { code: "+44", country: "GB", flag: "🇬🇧" }, { code: "+1", country: "US", flag: "🇺🇸" },
  { code: "+1", country: "CA", flag: "🇨🇦" }, { code: "+61", country: "AU", flag: "🇦🇺" },
  { code: "+64", country: "NZ", flag: "🇳🇿" }, { code: "+353", country: "IE", flag: "🇮🇪" },
  { code: "+49", country: "DE", flag: "🇩🇪" }, { code: "+33", country: "FR", flag: "🇫🇷" },
  { code: "+34", country: "ES", flag: "🇪🇸" }, { code: "+39", country: "IT", flag: "🇮🇹" },
  { code: "+31", country: "NL", flag: "🇳🇱" }, { code: "+46", country: "SE", flag: "🇸🇪" },
  { code: "+91", country: "IN", flag: "🇮🇳" }, { code: "+65", country: "SG", flag: "🇸🇬" },
  { code: "+971", country: "AE", flag: "🇦🇪" }, { code: "+55", country: "BR", flag: "🇧🇷" },
  { code: "+52", country: "MX", flag: "🇲🇽" }, { code: "+27", country: "ZA", flag: "🇿🇦" },
];

function PhoneInput({ countryCode, onCountryCodeChange, phoneNumber, onPhoneNumberChange, error }: {
  countryCode: string; onCountryCodeChange: (v: string) => void;
  phoneNumber: string; onPhoneNumberChange: (v: string) => void; error?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const selected = COUNTRY_CODES.find((c) => c.code === countryCode) || COUNTRY_CODES[0];
  const filtered = search ? COUNTRY_CODES.filter((c) => c.country.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search)) : COUNTRY_CODES;

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, fontWeight: 600, fontFamily: ff, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        Phone<span style={{ color: cyan }}> *</span>
      </label>
      <div style={{ display: "flex", gap: 7 }}>
        <div ref={ref} style={{ position: "relative" }}>
          <button type="button" onClick={() => { setOpen(!open); setSearch(""); }} style={{
            display: "flex", alignItems: "center", gap: 5,
            background: "rgba(255,255,255,0.04)", border: open ? `1px solid rgba(87,215,226,0.5)` : "1px solid rgba(255,255,255,0.1)",
            borderRadius: 8, padding: "11px 10px", color: "white", fontSize: 13, fontFamily: ff, cursor: "pointer", whiteSpace: "nowrap", minWidth: 82,
          }}>
            <span style={{ fontSize: 15 }}>{selected.flag}</span>
            <span>{selected.code}</span>
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
              <path d="M1 1l3 3 3-3" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {open && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 100,
              background: "#161616", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8,
              boxShadow: "0 8px 32px rgba(0,0,0,0.7)", width: 200, maxHeight: 220, overflow: "hidden", display: "flex", flexDirection: "column",
            }}>
              <div style={{ padding: 7 }}>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" autoFocus
                  style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, padding: "6px 9px", color: "white", fontSize: 12, fontFamily: ff, outline: "none" }}
                />
              </div>
              <div style={{ overflowY: "auto", maxHeight: 160, padding: "0 4px 4px" }}>
                {filtered.map((c) => (
                  <button key={`${c.country}-${c.code}`} type="button" onClick={() => { onCountryCodeChange(c.code); setOpen(false); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "6px 7px", background: c.code === countryCode && c.country === selected.country ? "rgba(87,215,226,0.08)" : "transparent", border: "none", borderRadius: 5, color: "white", fontSize: 12, fontFamily: ff, cursor: "pointer", textAlign: "left" }}>
                    <span style={{ fontSize: 13 }}>{c.flag}</span>
                    <span style={{ flex: 1 }}>{c.country}</span>
                    <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>{c.code}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <input type="tel" value={phoneNumber} onChange={(e) => onPhoneNumberChange(e.target.value)} placeholder="7700 900000"
          style={{ flex: 1, background: "rgba(255,255,255,0.04)", border: error ? "1px solid #ef4444" : "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "11px 14px", color: "white", fontSize: 15, fontFamily: ff, outline: "none", minWidth: 0 }}
          onFocus={(e) => { if (!error) e.currentTarget.style.borderColor = "rgba(87,215,226,0.5)"; }}
          onBlur={(e) => { if (!error) e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
        />
      </div>
      {error && <span style={{ color: "#f87171", fontSize: 11, fontFamily: ff }}>{error}</span>}
    </div>
  );
}

// ── Mobile detection ────────────────────────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    setMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
}

// ── Main ─────────────────────────────────────────────────────────────────
export default function TalkToAStrategist() {
  useSeoMeta({
    title: "Talk to an SEO Strategist | Honest Expert Guidance",
    description: "Speak directly with one of our SEO strategists. Get honest, expert guidance on your current search performance and what it would take to grow it.",
    canonical: "/talk-to-a-strategist",
    ogType: "website",
    jsonLd: [{
      "@type": "Service",
      name: "Free strategy session",
      provider: { "@type": "Organization", name: "Indexed", url: "https://www.joinindexed.com" },
      description: "A free 30-minute strategy session to identify what your site is losing in organic revenue.",
      url: "https://www.joinindexed.com/talk-to-a-strategist",
      offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
    }],
  });

  const isMobile = useIsMobile();
  const [, setLocation] = useLocation();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+44");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [monthlyBudget, setMonthlyBudget] = useState("");
  const [annualRevenue, setAnnualRevenue] = useState("");
  const [services, setServices] = useState<string[]>([]);

  const toggleService = (svc: string) => setServices((p) => p.includes(svc) ? p.filter((s) => s !== svc) : [...p, svc]);

  // Every field is required — a lead with no budget, revenue or services can't
  // be qualified before the call, so the form asks for all of it up front.
  const validateStep = (s: number) => {
    const errs: Record<string, string> = {};
    if (s === 0) {
      if (!name.trim()) errs.name = "Required";
      if (!email.trim()) errs.email = "Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Enter a valid email";
      if (!phoneNumber.trim()) errs.phone = "Required";
      else if (phoneNumber.replace(/\D/g, "").length < 6) errs.phone = "Enter a valid phone number";
    }
    if (s === 1) {
      if (!companyName.trim()) errs.companyName = "Required";
      if (!websiteUrl.trim()) errs.websiteUrl = "Required";
      else {
        try { new URL(websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`); }
        catch { errs.websiteUrl = "Enter a valid URL"; }
      }
      if (!annualRevenue) errs.annualRevenue = "Pick one";
    }
    if (s === 2) {
      if (!monthlyBudget) errs.monthlyBudget = "Pick one";
      if (services.length === 0) errs.services = "Pick at least one";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => { if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const handleBack = () => { setErrors({}); setStep((s) => Math.max(s - 1, 0)); };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;
    setSubmitting(true);
    try {
      const url = websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`;
      const res = await fetch("/api/submit-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(), email: email.trim(), companyName: companyName.trim(), websiteUrl: url,
          phone: `${countryCode} ${phoneNumber.trim()}`,
          monthlyBudget, annualRevenue, services,
          formSource: formSource("Talk to a Strategist"),
        }),
      });
      if (!res.ok) throw new Error("Submission failed. Please try again.");
      // Fire conversion events on the trackers loaded in index.html
      const w = window as any;
      try {
        w.clarity?.("event", "lead_form_submit");
        w.fbq?.("track", "Lead");
        w.gtag?.("event", "generate_lead", { event_category: "lead", event_label: "talk_to_a_strategist" });
      } catch { /* tracking must never block the redirect */ }
      setLocation("/book-a-call");
    } catch (err: any) {
      setErrors({ submit: err?.message || "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const progressBar = (
    <div style={{ flexShrink: 0, padding: isMobile ? "20px 20px 0" : "28px 48px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 6 }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 5 : 8 }}>
              <div style={{
                width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                background: i < step ? cyan : i === step ? "rgba(87,215,226,0.12)" : "rgba(255,255,255,0.04)",
                border: i <= step ? `1.5px solid ${cyan}` : "1.5px solid rgba(255,255,255,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {i < step ? (
                  <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                    <path d="M1 4l3.5 3L10 1" stroke="#0c0c0c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <span style={{ color: i === step ? cyan : "rgba(255,255,255,0.2)", fontSize: 11, fontWeight: 700, fontFamily: ff }}>{i + 1}</span>
                )}
              </div>
              {!isMobile && (
                <span style={{ color: i === step ? "rgba(255,255,255,0.9)" : i < step ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.2)", fontSize: 12, fontWeight: i === step ? 600 : 400, fontFamily: ff, whiteSpace: "nowrap" }}>
                  {s.label}
                </span>
              )}
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1, margin: isMobile ? "0 8px" : "0 12px", background: i < step ? "rgba(87,215,226,0.35)" : "rgba(255,255,255,0.06)" }} />
            )}
          </React.Fragment>
        ))}
      </div>
      {isMobile && (
        <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, fontFamily: ff, marginBottom: 4 }}>
          Step {step + 1} of {STEPS.length} — {STEPS[step].label}
        </div>
      )}
      <div style={{ height: 2, background: "rgba(255,255,255,0.05)", borderRadius: 2, overflow: "hidden", marginTop: isMobile ? 6 : 16 }}>
        <div style={{ height: "100%", width: `${((step + 1) / STEPS.length) * 100}%`, background: `linear-gradient(90deg, ${cyan}, #20b0bc)`, borderRadius: 2, transition: "width 0.4s ease" }} />
      </div>
    </div>
  );

  const stepHeading = (
    <div style={{ flexShrink: 0, padding: isMobile ? "16px 20px 0" : "20px 48px 0" }}>
      <h2 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: "white", margin: 0, fontFamily: ff, letterSpacing: "-0.01em" }}>
        {step === 0 ? "Your details" : step === 1 ? "Your company" : "Your goals"}
      </h2>
      <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, margin: "4px 0 0", fontFamily: ff }}>
        {step === 0 ? "How should we reach you?" : step === 1 ? "Tell us about your business." : "What are you looking to achieve?"}
      </p>
    </div>
  );

  const formFields = (
    <div className="form-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: isMobile ? "16px 20px 0" : "20px 48px 0" }}>
      {step === 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Full Name" value={name} onChange={setName} placeholder="Alex Johnson" required error={errors.name} />
          <Field label="Email Address" type="email" value={email} onChange={setEmail} placeholder="alex@company.com" required error={errors.email} />
          <PhoneInput countryCode={countryCode} onCountryCodeChange={setCountryCode} phoneNumber={phoneNumber} onPhoneNumberChange={setPhoneNumber} error={errors.phone} />
        </div>
      )}
      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Company Name" value={companyName} onChange={setCompanyName} placeholder="Acme Inc." required error={errors.companyName} />
          <Field label="Website URL" value={websiteUrl} onChange={setWebsiteUrl} placeholder="acme.com" required error={errors.websiteUrl} />
          <GridSelect label="Annual Revenue" options={REVENUE_OPTIONS} value={annualRevenue} onChange={setAnnualRevenue} error={errors.annualRevenue} />
        </div>
      )}
      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <GridSelect label="Monthly Growth Budget" options={BUDGET_OPTIONS} value={monthlyBudget} onChange={setMonthlyBudget} error={errors.monthlyBudget} />
          <PillSelect label="Services you're interested in" options={SERVICE_OPTIONS} selected={services} onToggle={toggleService} error={errors.services} />
        </div>
      )}
    </div>
  );

  const navButtons = (
    <>
      {errors.submit && (
        <div style={{ flexShrink: 0, margin: isMobile ? "8px 20px 0" : "8px 48px 0", padding: "10px 14px", borderRadius: 8, background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", color: "#f87171", fontSize: 13, fontFamily: ff }}>
          {errors.submit}
        </div>
      )}
      <div style={{ flexShrink: 0, padding: isMobile ? "14px 20px 24px" : "16px 48px 32px", display: "flex", gap: 10, alignItems: "center" }}>
        {step > 0 && (
          <button type="button" onClick={handleBack} style={{
            padding: "0 20px", height: 50, borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.1)", background: "transparent",
            color: "rgba(255,255,255,0.5)", fontSize: 14, fontWeight: 600, fontFamily: ff, cursor: "pointer", flexShrink: 0,
          }}>
            Back
          </button>
        )}
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={handleNext} style={{
            flex: 1, height: 50, borderRadius: 10,
            border: `1px solid rgba(87,215,226,0.6)`,
            background: "linear-gradient(135deg, #6ddbe4 0%, #1aacb8 100%)",
            boxShadow: "0 4px 20px rgba(87,215,226,0.18)",
            fontSize: 15, fontWeight: 700, fontFamily: ff, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <span style={{ color: "#071415" }}>Continue</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7h8M7 3l4 4-4 4" stroke="#071415" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : (
          <button type="button" onClick={handleSubmit} disabled={submitting} style={{
            flex: 1, height: 50, borderRadius: 10,
            border: `1px solid rgba(87,215,226,0.6)`,
            background: submitting ? "rgba(87,215,226,0.2)" : "linear-gradient(135deg, #6ddbe4 0%, #1aacb8 100%)",
            boxShadow: submitting ? "none" : "0 4px 20px rgba(87,215,226,0.18)",
            color: "#071415", fontSize: 15, fontWeight: 700, fontFamily: ff,
            cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1,
          }}>
            {submitting ? "Submitting…" : "Get My Free Analysis"}
          </button>
        )}
      </div>
    </>
  );

  // ── Mobile layout ─────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", background: "#0c0c0c", fontFamily: ff }}>
        <style>{`.form-scroll::-webkit-scrollbar { display: none; } .form-scroll { -ms-overflow-style: none; scrollbar-width: none; }`}</style>

        {/* Mobile header bar */}
        <div style={{
          flexShrink: 0, background: "#080a0a", borderBottom: "1px solid rgba(255,255,255,0.06)",
          backgroundImage: GRID_BG, backgroundSize: "162px 162px", position: "relative",
        }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(8,10,10,0.4), rgba(8,10,10,0.85))", pointerEvents: "none" }} />
          <div style={{ position: "relative", zIndex: 1, padding: "20px 20px 24px" }}>
            <a href="/" style={{ display: "inline-block", marginBottom: 16 }}>
              <img src={INDEXED_WORDMARK} alt="Indexed" width="96" height="16" style={{ width: 96, height: 16, objectFit: "contain" }} />
            </a>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: "white", lineHeight: 1.15, letterSpacing: "-0.02em", margin: "0 0 6px", fontFamily: ff }}>
              Talk to a <span style={{ color: cyan }}>Strategist.</span>
            </h1>
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, margin: 0, fontFamily: ff }}>
              Free 30-min strategy call · no pressure
            </p>
            {/* Social proof inline */}
            <div style={{ display: "flex", gap: 20, marginTop: 14 }}>
              {[{ stat: "5.0 ★", label: "100+ reviews" }, { stat: "3.5×", label: "Avg ROI" }, { stat: "Free", label: "No cost" }].map((s) => (
                <div key={s.stat}>
                  <div style={{ color: cyan, fontSize: 14, fontWeight: 700, fontFamily: ff, lineHeight: 1 }}>{s.stat}</div>
                  <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 10, fontFamily: ff, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form panel — scrolls naturally */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#0e1011" }}>
          {progressBar}
          {stepHeading}
          {formFields}
          {navButtons}
        </div>
      </div>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", height: "100dvh", overflow: "hidden", background: "#0c0c0c", fontFamily: ff }}>
      <style>{`.form-scroll::-webkit-scrollbar { display: none; } .form-scroll { -ms-overflow-style: none; scrollbar-width: none; }`}</style>

      {/* ── LEFT PANEL ── */}
      <div style={{
        width: "42%", flexShrink: 0, display: "flex", flexDirection: "column",
        background: "#080a0a", position: "relative", overflow: "hidden",
        backgroundImage: GRID_BG, backgroundSize: "162px 162px",
      }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(to bottom, rgba(8,10,10,0) 0%, rgba(8,10,10,0.5) 50%, rgba(8,10,10,0.95) 100%)" }} />
        <div style={{ position: "absolute", top: -80, left: -80, width: 400, height: 400, background: "radial-gradient(circle, rgba(87,215,226,0.07) 0%, transparent 65%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", height: "100%", padding: "32px 44px" }}>
          <a href="/" style={{ display: "inline-block", marginBottom: "auto" }}>
            <img src={INDEXED_WORDMARK} alt="Indexed" width="110" height="18" style={{ width: 110, height: 18, objectFit: "contain" }} />
          </a>

          <div style={{ marginBottom: 48 }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", color: cyan, textTransform: "uppercase", marginBottom: 16, fontFamily: ff }}>
              Free strategy session
            </div>
            <h1 style={{ fontSize: 38, fontWeight: 700, color: "white", lineHeight: 1.1, letterSpacing: "-0.025em", margin: "0 0 20px", fontFamily: ff }}>
              Talk to a<br /><span style={{ color: cyan }}>Strategist.</span>
            </h1>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 15, lineHeight: 1.65, margin: "0 0 32px", fontFamily: ff }}>
              Tell us about your business and we'll show you exactly what you're leaving on the table in organic search.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                "30-minute strategy call, zero pressure",
                "Honest assessment of your current SEO",
                "Clear growth roadmap you can act on today",
              ].map((item) => (
                <div key={item} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(87,215,226,0.12)", border: `1px solid rgba(87,215,226,0.3)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                    <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                      <path d="M1 3l2 2 4-4" stroke={cyan} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span style={{ color: "rgba(255,255,255,0.65)", fontSize: 14, lineHeight: 1.5, fontFamily: ff }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Social proof */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 24 }}>
            <div style={{ display: "flex", gap: 28 }}>
              {[
                { stat: "5.0 ★", label: "100+ reviews" },
                { stat: "3.5×", label: "Average ROI" },
                { stat: "3–4", label: "Clients/month" },
              ].map((s) => (
                <div key={s.stat}>
                  <div style={{ color: cyan, fontSize: 17, fontWeight: 700, fontFamily: ff, lineHeight: 1 }}>{s.stat}</div>
                  <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 11, fontFamily: ff, marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#0e1011", borderLeft: "1px solid rgba(255,255,255,0.05)" }}>
        {progressBar}
        {stepHeading}
        {formFields}
        {navButtons}
      </div>
    </div>
  );
}

